<script setup lang="ts">
import { useStore } from "vuex";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { validateFile } from "common/utils/file";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { useDtfStore } from "components/jiffy/vue-product-details-dtf/store";
import CanvaButton from "components/jiffy/canva-button/CanvaButton.vue";
import { trackUploadInitiated } from "../utils/tracker";
import { MODES } from "../misc/constants";

const props = defineProps({
  sideKey: {
    type: String,
    required: true
  }
});

// Ref variables
const draggedOver = ref(false);

// Setup store
const store = useStore();

// Compute properties
const fileInputId = computed(() => `field-artwork-upload-${props.sideKey}`);
const enableMonetization = computed(() => store.getters.config.enable_ab_made_monetization_v2);
const dtfStore = useDtfStore();
const uploadFromCanvaEnabled = computed(() => store.getters.config.canva_dtf_enabled);
const index = computed(() => (props.sideKey === "front" ? 0 : 1));
const isUserSignedIn = computed(() => store.getters.userType === "signed_in");
const canvaSettings = computed(() => store.getters.canvaSettings);

// Methods
function uploadArtwork(file: File) {
  if (!store.getters["app/supportsMade"]) {
    window.alert("This product color is not supported in Jiffy Made.");
    return;
  }

  const extensions = ["png", "gif", "jpeg", "jpg", "svg", "svg+xml", "heic", "heif"];
  const accept = extensions.map(extension => `image/${extension}`);
  const extension = `(${extensions.join("|")})`;

  if (validateFile(file, { extension, accept })) {
    trackUploadInitiated(props.sideKey, store.getters["app/mode"], file);
    store.commit("app/setMode", MODES.MADE);
    store.dispatch(`artwork_${props.sideKey}/upload`, file);
  }
}

function preventDefaults(e) {
  e.preventDefault();
}

function handleDrop(event: DragEvent) {
  draggedOver.value = false;
  const { files } = event.dataTransfer!;
  if (files && files.length > 0) {
    uploadArtwork(files[0] as File);
  }
}

function openArtworkHistoryModal() {
  store.commit("app/setMode", MODES.MADE);
  store.commit("setArtworkHistoryModalIndex", index.value);
  dtfStore.setShowArtworkHistoryModal(true);
}

// Prevent default drag behaviors
const events = ["dragenter", "dragover", "dragleave", "drop"];
onMounted(() => {
  events.forEach(eventName => {
    document.body.addEventListener(eventName, preventDefaults);
  });
});
onUnmounted(() => {
  events.forEach(eventName => {
    document.body.removeEventListener(eventName, preventDefaults);
  });
});
</script>

<template>
  <div
    class="artwork-upload-buttons"
    :class="{
      'artwork-upload-buttons--dragged-over': draggedOver,
      'ab-made-monetization-enabled': enableMonetization
    }"
    @dragenter="draggedOver = true"
    @dragover="draggedOver = true"
    @dragleave="draggedOver = false"
    @drop="handleDrop"
  >
    <UseSvg icon="cloud-upload" class="artwork-upload-buttons__icon" />
    <p class="artwork-upload-buttons__title">Drag your design here</p>
    <p class="artwork-upload-buttons__divider">Or choose a design from</p>

    <label
      class="artwork-upload-buttons__button artwork-upload-buttons__button--upload"
      data-testid="artwork-upload"
    >
      My Device
      <input
        :id="fileInputId"
        accept="image/*"
        class="artwork-upload-buttons__input"
        type="file"
        @change="uploadArtwork(($event.target as HTMLInputElement).files![0]!)"
      />
    </label>

    <button
      v-if="isUserSignedIn"
      type="button"
      class="artwork-upload-buttons__button artwork-upload-buttons__button--link"
      @click.prevent="openArtworkHistoryModal"
    >
      My Design Library
    </button>

    <div v-else class="artwork-upload-buttons__sign-in">
      <a class="artwork-upload-buttons__link" href="/login" @click.stop>Log in</a>
      to see purchased designs.
    </div>

    <div
      v-if="uploadFromCanvaEnabled && isUserSignedIn"
      class="artwork-upload-buttons__button artwork-upload-buttons__button--link"
      :class="{ 'artwork-upload-buttons__button--disabled': !canvaSettings }"
      @click.stop
    >
      <CanvaButton
        :key="canvaSettings?.authorized"
        :file-landing="`#${fileInputId}`"
        namespace="dtf"
        :settings="canvaSettings"
        skip-settings-loading
        skip-initial-modal-show
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/button/mixin";

.artwork-upload-buttons {
  pointer-events: all;
  border: 1px dashed $primary-light;
  background: $spindle-light;
  border-radius: 1px;
  width: 192px;
  margin: 0 auto;
  padding: 14px 16px;
  box-shadow:
    0 0 0 6px $spindle-light,
    0 0 0 8px $spindle-dark;
  position: relative;
  font-family: $font-family-secondary;

  &.ab-made-monetization-enabled {
    position: absolute;
    top: 200px;
    left: 50%;
    transform: translateX(-50%);
  }

  &--dragged-over {
    > * {
      visibility: hidden;
    }

    &::after {
      position: absolute;
      inset: 0;
      margin: auto;
      content: "Drop your design here";
      font-size: 14px;
      line-height: 20px;
      height: 20px;
      color: $pod-gray-modern-900;
      text-align: center;
      font-weight: 600;
      font-family: $digital-assets-font-family;
    }
  }

  &__button {
    @include clear-button;

    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $primary-light;
    border: 1px solid $primary-light;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    height: 40px;
    border-radius: 8px;

    & + & {
      margin-top: 8px;
    }

    &--upload {
      background: $primary-light;
      color: $white;
      position: relative;
      overflow: hidden;
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }

  &__input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }

  &__icon,
  &__title,
  &__divider {
    display: none;

    @include with-container(container, $pod-pdp-desktop-response) {
      display: block;
    }
  }

  &__icon {
    margin: 0 auto 8px;
    width: 35px;
    height: 35px;
    color: $primary-light;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: $pod-gray-modern-900;
    text-align: center;
    font-weight: 600;
    font-family: $digital-assets-font-family;
  }

  &__divider {
    margin: 0 0 12px;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    color: $pod-gray-modern-600;
    font-family: $digital-assets-font-family;
  }

  &__sign-in {
    text-align: center;
  }

  &__link {
    text-decoration: underline;
    font-weight: 700;
  }
}
</style>
